import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import NoticiasSidebar from "./components/NoticiasSidebar";
import AppHeader, { DevBanner } from "./components/layout/AppHeader";
import { registrarRecursoReciente } from "./utils/recientes";
import { emitFavoritosActualizados } from "./utils/favoritos";
import { Info, LayoutGrid, ChevronLeft, ChevronRight, Video } from "lucide-react";
import "./App.css";
import axios from "axios";
import Metabuscador from "./components/Metabuscador";
import DOMPurify from "dompurify";
import CategoriaPage from "./pages/CategoriaPage";
import RecursoPage from "./pages/RecursoPage";

/* =========================
   CONFIG DE ENTORNO / MODOS (CRA)
   ========================= */
const MODO_MANTENIMIENTO = process.env.REACT_APP_MODO_MANTENIMIENTO === "true";
const MODO_DESARROLLO = process.env.REACT_APP_MODO_DESARROLLO === "true";

/* Alturas/offsets coherentes */
const HEADER_BASE_HEIGHT = 75;
const DEV_BANNER_HEIGHT = 40;

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";
const LOGIN_BASE = "https://login.vpn.ucacue.edu.ec/login";

// Compatibilidad con el flujo PHP (index.php)
const ACTIVAR_INGRESO_ANONIMO = process.env.REACT_APP_ACTIVAR_INGRESO_ANONIMO === "true";
// Código de acceso anónimo (en PHP era "HYu2364579")
const ANON_ACCESS_CODE = process.env.REACT_APP_ANON_ACCESS_CODE || "HYu2364579";

function isDevHost() {
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

function getAuthValue(key) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

function setAuthValueLocal(key, value) {
  if (value === null || value === undefined) localStorage.removeItem(key);
  else localStorage.setItem(key, String(value));
  // Evita inconsistencias
  sessionStorage.removeItem(key);
}

function clearAuthRuntimeFlags() {
  const runtimeKeys = ["guest_log_sent", "anon_log_sent", "auth_log_sent"];
  for (const k of runtimeKeys) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
  window.__logInProgress = false;
}

function clearAuthAll() {
  const keys = ["token", "guest", "anonimo", "guest_code", "user_sso", "user_sso_roles"];
  for (const k of keys) {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  }
  clearAuthRuntimeFlags();
  window.dispatchEvent(new Event("auth_changed"));
}

function getAuthSnapshot() {
  const token = getAuthValue("token");
  const guest = getAuthValue("guest") === "true";
  const anon = getAuthValue("anonimo") === "true";

  const userRaw = getAuthValue("user_sso");
  const user = userRaw ? safeParse(userRaw) : null;
  console.debug("Auth snapshot:", { token, guest, anon, user });

  // “Autenticado real” = token + NO guest + NO anon
  const isAuth = !!token && !guest && !anon;

  // Similar al PHP: Perfil: NOMBRES (y en caso guest/anon etiquetas)
  const label = anon
    ? "ANÓNIMO"
    : guest
      ? "INVITADO"
      : (user?.nombres || user?.identificacion || "Perfil");

  return { token, guest, anon, isAuth, label };
}

function hasValidSession() {
  const token = getAuthValue("token");

  // 1. Validaciones básicas de existencia
  if (!token || token === "null" || token === "undefined") return false;

  try {
    // 2. Separar el token (formato: header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // 3. Decodificar el Payload (la parte media del JWT)
    // Usamos atob para decodificar Base64
    const payload = JSON.parse(atob(parts[1]));

    // 4. Validar la fecha de expiración (exp)
    if (payload.exp) {
      const ahora = Math.floor(Date.now() / 1000); // Tiempo actual en segundos

      if (ahora >= payload.exp) {
        console.warn("El token ha expirado.");
        // Opcional: podrías llamar a clearAuthAll() aquí mismo
        return false;
      }
    }

    return true; // El token existe, tiene formato correcto y no ha expirado
  } catch (error) {
    console.error("Error al validar el token:", error);
    return false;
  }
}


function getApiBaseAbs() {
  // API_BASE puede ser "/api" o una URL absoluta
  const base = API_BASE.startsWith("http")
    ? API_BASE
    : `${window.location.origin}${API_BASE}`;
  return base.replace(/\/$/, "");
}

function buildEzproxyLoginUrl(nextRel) {
  // nextRel debe ser relativo y empezar con "/"
  const apiAbs = getApiBaseAbs();
  const callbackUrl = `${apiAbs}/sso/ezproxy/callback?next=${encodeURIComponent(
    nextRel
  )}`;
  return `${LOGIN_BASE}?url=${encodeURIComponent(callbackUrl)}`;
}

// ✅ Apertura de recursos SIEMPRE por backend, y si existe sesión JWT, se envía por query (?token=...)
// (Opción A) — evita redirecciones innecesarias a EZproxy cuando el backend necesita identificar al usuario
function buildRecursoGoUrl(apiBase, recursoId) {
  const base = String(apiBase || API_BASE || "/api").replace(/\/$/, "");
  const href = `${base}/recursos/${recursoId}/go`;
  const token = getAuthValue("token");
  if (!token) return href;
  return `${href}?token=${encodeURIComponent(token)}`;
}

function hasTutorialUrl(recurso) {
  return !!String(recurso?.urlTutorial || "").trim();
}

function TutorialLink({ recurso, className = "" }) {
  if (!hasTutorialUrl(recurso)) return null;

  return (
    <a
      href={String(recurso.urlTutorial).trim()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={className}
      title="Ver tutorial"
      aria-label={`Ver tutorial de ${recurso.nombre}`}
    >
      <Video className="w-4 h-4" />
      <span>Tutorial</span>
    </a>
  );
}

function isAnonParamValid(anValue) {
  if (!anValue) return false;

  // 1) Compatibilidad actual del React: ?an=1
  if (anValue === "1") return true;

  // 2) Compatibilidad PHP: ?an=HYu2364579 (configurable)
  if (anValue === ANON_ACCESS_CODE) return true;

  // 3) Compatibilidad histórica (si alguien envía base64("ANONIMO"))
  //    base64("ANONIMO") = QU5PTklNTw==
  try {
    const decoded = atob(anValue);
    if ((decoded || "").toUpperCase() === "ANONIMO") return true;
  } catch { }

  return false;
}

const FALLBACK_RESOURCE_IMAGE = "/placeholder.png";
const resourceImageCache = new Map();
const resourceImageInFlight = new Map();
const resourceImageFailCache = new Map();
const RESOURCE_IMAGE_RETRY_DELAY_MS = 300;
const RESOURCE_IMAGE_FAIL_TTL_MS = 20000;

function isDataImageUrl(value) {
  return typeof value === "string" && value.startsWith("data:image/");
}

async function fetchResourceImage(id) {
  if (!id) return FALLBACK_RESOURCE_IMAGE;

  if (resourceImageCache.has(id)) {
    return resourceImageCache.get(id);
  }

  if (resourceImageInFlight.has(id)) {
    return resourceImageInFlight.get(id);
  }

  const lastFailure = resourceImageFailCache.get(id);
  if (
    typeof lastFailure === "number" &&
    Date.now() - lastFailure < RESOURCE_IMAGE_FAIL_TTL_MS
  ) {
    return FALLBACK_RESOURCE_IMAGE;
  }

  const loadOnce = async () => {
    const res = await fetch(`${API_BASE}/recursos/${id}/imagen`);
    if (!res.ok) throw new Error("image_fetch_failed");
    const txt = await res.text();
    if (!isDataImageUrl(txt)) throw new Error("invalid_image_payload");
    return txt;
  };

  const promise = (async () => {
    try {
      const src = await loadOnce();
      resourceImageCache.set(id, src);
      resourceImageFailCache.delete(id);
      return src;
    } catch {
      try {
        await new Promise((resolve) =>
          setTimeout(resolve, RESOURCE_IMAGE_RETRY_DELAY_MS),
        );
        const src = await loadOnce();
        resourceImageCache.set(id, src);
        resourceImageFailCache.delete(id);
        return src;
      } catch {
        resourceImageFailCache.set(id, Date.now());
        return FALLBACK_RESOURCE_IMAGE;
      }
    }
  })()
    .catch(() => {
      return FALLBACK_RESOURCE_IMAGE;
    })
    .finally(() => {
      resourceImageInFlight.delete(id);
    });

  resourceImageInFlight.set(id, promise);
  return promise;
}


const LazyResourceImage = React.memo(function LazyResourceImage({
  resourceId,
  alt,
  className,
  eager = false,
}) {
  const imgRef = useRef(null);
  const [isVisible, setIsVisible] = useState(
    eager || resourceImageCache.has(resourceId),
  );
  const [src, setSrc] = useState(
    resourceImageCache.get(resourceId) || FALLBACK_RESOURCE_IMAGE,
  );

  useEffect(() => {
    setSrc(resourceImageCache.get(resourceId) || FALLBACK_RESOURCE_IMAGE);
    setIsVisible(eager || resourceImageCache.has(resourceId));
  }, [resourceId, eager]);

  useEffect(() => {
    if (!imgRef.current || isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0.01 },
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !resourceId) return;
    let cancelled = false;

    fetchResourceImage(resourceId).then((nextSrc) => {
      if (cancelled) return;
      setSrc(nextSrc);
    });

    return () => {
      cancelled = true;
    };
  }, [isVisible, resourceId]);

  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      className={className}
      loading={eager ? "eager" : "lazy"}
      decoding="async"
      onError={(e) => {
        if (resourceId) {
          resourceImageCache.delete(resourceId);
          resourceImageFailCache.set(resourceId, Date.now());
        }
        e.currentTarget.src = FALLBACK_RESOURCE_IMAGE;
      }}
    />
  );
});

/* =========================
   UI: SPOTLIGHT (Hero) estilo Microsoft Store (adaptado)
   ========================= */

const SpotlightHero = React.memo(function SpotlightHero({
  items,
  index,
  setIndex,
  getImgSrc,
  apiBase,
  favoritos,
  onToggleFav,
  onInfo,
  onOpen,
}) {
  if (!Array.isArray(items) || items.length === 0) return null;

  const main = items[Math.max(0, Math.min(index, items.length - 1))];

  const sideOffsets = [1, 2, 3].filter((o) => o < items.length);
  const sideCards = sideOffsets.map((o) => items[(index + o) % items.length]);

  const img = getImgSrc(main.id);

  const chip = (label) => (
    <span className="px-2.5 py-1 text-[11px] rounded-full bg-white/80 border border-white/60 text-gray-800 backdrop-blur">
      {label}
    </span>
  );

  return (
    <section className="mb-8">
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* Main spotlight */}
        <div className="xl:col-span-8">
          <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-black shadow-sm">
            {/* Background */}
            <div
              className="absolute inset-0 scale-110 blur-2xl opacity-40"
              style={{
                backgroundImage: `url(${img})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />

            {/* Foreground */}
            <div className="relative p-5 md:p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {main?.categoria?.nombre && chip(main.categoria.nombre)}
                  {Array.isArray(main?.areas) &&
                    main.areas.slice(0, 3).map((a) => (
                      <span
                        key={a.id || a.nombre}
                        className="px-2.5 py-1 text-[11px] rounded-full bg-white/20 border border-white/20 text-white"
                      >
                        {a.nombre}
                      </span>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onToggleFav(main)}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white"
                    title="Favorito"
                    aria-label="Favorito"
                  >
                    {favoritos?.some((r) => r.id === main.id) ? "❤️" : "🤍"}
                  </button>
                  <button
                    onClick={() => onInfo(main)}
                    className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white"
                    title="Información"
                    aria-label="Información"
                  >
                    <Info className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
                <div className="md:col-span-7">
                  <h2 className="text-white text-2xl md:text-3xl font-semibold leading-tight">
                    {main.nombre}
                  </h2>
                  <p className="text-white/80 mt-2 text-sm md:text-base">
                    Explora este recurso y descubre contenido relevante para tu
                    estudio e investigación.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={buildRecursoGoUrl(apiBase, main.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onOpen(main)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-gray-900 hover:bg-gray-100 text-sm font-semibold"
                    >
                      <LayoutGrid className="w-4 h-4" />
                      Abrir recurso
                    </a>

                    <TutorialLink
                      recurso={main}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-semibold"
                    />

                    <button
                      onClick={() => setIndex((i) => (i + 1) % items.length)}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm"
                    >
                      Siguiente
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="md:col-span-5">
                  <div className="relative rounded-2xl overflow-hidden border border-white/15 bg-white/5">
                    <img
                      src={img}
                      alt={main.nombre}
                      className="w-full h-[220px] md:h-[260px] object-cover"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.png";
                      }}
                    />
                  </div>

                  {/* Dots */}
                  <div className="mt-3 flex items-center justify-center gap-2">
                    {items.slice(0, Math.min(items.length, 7)).map((_, i) => (
                      <button
                        key={i}
                        className={`h-2 rounded-full transition-all ${i === index ? "w-7 bg-white" : "w-2 bg-white/40 hover:bg-white/60"}`}
                        onClick={() => setIndex(i)}
                        aria-label={`Ir al destacado ${i + 1}`}
                        title={`Ir al destacado ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Side cards */}
        <div className="xl:col-span-4 grid grid-rows-2 gap-4 h-full">

          {/* ===== CARD GRANDE SUPERIOR ===== */}
          {sideCards[0] && (
            <div className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm group">

              <img
                src={getImgSrc(sideCards[0].id)}
                alt={sideCards[0].nombre}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => (e.currentTarget.src = "/placeholder.png")}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

              <div className="relative z-10 h-full flex flex-col justify-between p-5 text-white">

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => onToggleFav(sideCards[0])}
                    className="w-9 h-9 rounded-full bg-white/20 backdrop-blur hover:bg-white/30"
                  >
                    {favoritos?.some((r) => r.id === sideCards[0].id) ? "❤️" : "🤍"}
                  </button>

                  <button
                    onClick={() => onInfo(sideCards[0])}
                    className="w-9 h-9 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 flex items-center justify-center"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>

                <div>
                  <div className="text-xs opacity-80">
                    {sideCards[0]?.categoria?.nombre || "Recurso"}
                  </div>

                  <div className="font-semibold text-lg leading-tight">
                    {sideCards[0].nombre}
                  </div>

                  <a
                    href={buildRecursoGoUrl(apiBase, sideCards[0].id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onOpen(sideCards[0])}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-900 hover:bg-gray-100 text-sm font-semibold"
                  >
                    Abrir
                    <ChevronRight className="w-4 h-4" />
                  </a>

                  <TutorialLink
                    recurso={sideCards[0]}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-white hover:bg-white/25 text-sm font-semibold"
                  />
                </div>
              </div>

              <button
                className="absolute inset-0"
                onClick={() => setIndex((i) => (i + 1) % items.length)}
              />
            </div>
          )}

          {/* ===== FILA INFERIOR ===== */}
          <div className="grid grid-cols-2 gap-4">

            {sideCards.slice(1, 3).map((it, idx) => (
              <div
                key={it.id}
                className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm group"
              >
                <div
                  className="absolute inset-0 transition-transform duration-500 group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${getImgSrc(it.id)})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />

                <div className="absolute inset-0 bg-black/55" />

                <div className="relative z-10 h-full flex flex-col justify-between p-4 text-white">

                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onToggleFav(it)}
                      className="w-8 h-8 rounded-full bg-white/20 backdrop-blur hover:bg-white/30"
                    >
                      {favoritos?.some((r) => r.id === it.id) ? "❤️" : "🤍"}
                    </button>

                    <button
                      onClick={() => onInfo(it)}
                      className="w-8 h-8 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 flex items-center justify-center"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>

                  <div>
                    <div className="text-[11px] opacity-80">
                      {it?.categoria?.nombre || "Recurso"}
                    </div>

                    <div className="font-semibold text-sm truncate">
                      {it.nombre}
                    </div>

                    <a
                      href={buildRecursoGoUrl(apiBase, it.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onOpen(it)}
                      className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white text-gray-900 hover:bg-gray-100 text-xs font-semibold"
                    >
                      Abrir
                    </a>

                    <TutorialLink
                      recurso={it}
                      className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/15 text-white hover:bg-white/25 text-xs font-semibold"
                    />
                  </div>
                </div>

                <button
                  className="absolute inset-0"
                  onClick={() => setIndex((i) => (i + idx + 2) % items.length)}
                />
              </div>
            ))}

          </div>
        </div>
      </div>
    </section>
  );
});

/* =========================
   UI: PANTALLA MANTENIMIENTO
   ========================= */
function MaintenanceScreen() {
  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gray-100">
      <div className="bg-amber-600 w-full text-center py-3 text-white font-bold text-lg shadow">
        Página en Mantenimiento
      </div>
      <div className="max-w-lg bg-white rounded-xl shadow p-8 mt-6 text-center">
        <h1 className="text-2xl font-bold mb-2">Biblioteca Virtual</h1>
        <p className="text-gray-600">
          Estamos realizando ajustes para mejorar tu experiencia. <br />
          Por favor, vuelve a intentarlo más tarde.
        </p>
      </div>
    </div>
  );
}

/* =========================
   HELPERS FRONT: LOG DE USO
   ========================= */
/* === Payload para ANONIMO === */
function buildAnonUser() {
  return {
    codigoErp: "99999999999",
    tipoIdentificacion: "CEDULA",
    identificacion: "99999999999",
    nombres: "ANONIMO",
    apellidos: "(Acceso público)",
    numeroCelular: "",
    emailInstitucional: "anonimo@ucacue.edu.ec",
    emailPersonal: "",
    sexo: "N/A",
    rolesUsuario: [
      {
        tipoRol: "ANONIMO",
        detallesRol: [
          {
            nombreCarrera: "Acceso Público",
            unidadAcademica: "Visitante",
            sede: "Externo",
            modalidad: "Web",
            curso: "",
            paralelo: "",
            nombreRol: "ANONIMO",
            unidadOrganizativa: "Visitante",
          },
        ],
      },
    ],
  };
}

/* === Payload para INVITADO (visitante) === */
function buildGuestUser(invCode) {
  return {
    codigoErp: "99999999999",
    tipoIdentificacion: "CEDULA",
    identificacion: invCode || "99999999999",
    nombres: "INVITADO",
    apellidos: "(Acceso público)",
    numeroCelular: "",
    emailInstitucional: "invitado@ucacue.edu.ec",
    emailPersonal: "",
    sexo: "N/A",
    rolesUsuario: [
      {
        tipoRol: "INVITADO",
        detallesRol: [
          {
            nombreCarrera: "Acceso Público",
            unidadAcademica: "Visitante",
            sede: "Externo",
            modalidad: "Web",
            curso: "",
            paralelo: "",
            nombreRol: "INVITADO",
            unidadOrganizativa: "Visitante",
          },
        ],
      },
    ],
  };
}

function buildUsagePayloadsByRole(userDTO) {
  if (
    !userDTO ||
    !Array.isArray(userDTO.rolesUsuario) ||
    userDTO.rolesUsuario.length === 0
  ) {
    return [userDTO];
  }

  const payloads = [];

  for (const rol of userDTO.rolesUsuario) {
    const tipoRol = rol?.tipoRol || "SIN_ROL";
    const detalles =
      Array.isArray(rol?.detallesRol) && rol.detallesRol.length > 0
        ? rol.detallesRol
        : [null];

    for (const det of detalles) {
      const singleRole = {
        tipoRol,
        detallesRol: det ? [det] : [],
      };

      payloads.push({
        ...userDTO,
        rolesUsuario: [singleRole],

        rolActual: tipoRol,
        detalleActual: det || null,
      });
    }
  }

  return payloads;
}

function pickText(...values) {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const t = String(v).trim();
    if (t) return t;
  }
  return "";
}

function normalizeRolesUsuarioFromSso(user, sourceList) {
  if (Array.isArray(user?.rolesUsuario) && user.rolesUsuario.length > 0) {
    return user.rolesUsuario;
  }

  const src =
    Array.isArray(sourceList) && sourceList.length > 0 ? sourceList : [user];

  const roles = src
    .filter((it) => it && typeof it === "object")
    .map((it) => {
      const tipoRol = pickText(it.tipoRol, it.tipo_usuario, it.cargo, "USER");
      const detalle = {
        idCarrera: pickText(it.idCarrera, it.id_carrera),
        nombreCarrera: pickText(it.nombreCarrera, it.carrera),
        ultimoSemestreActivo: pickText(
          it.ultimoSemestreActivo,
          it.ultimo_semestre_activo,
        ),
        unidadAcademica: pickText(it.unidadAcademica, it.unidad_academica),
        unidadOrganizativa: pickText(it.unidadOrganizativa, it.departamento),
        sede: pickText(it.sede),
        modalidad: pickText(it.modalidad),
        curso: pickText(it.curso),
        paralelo: pickText(it.paralelo),
        nombreRol: pickText(it.nombreRol, it.cargo, it.tipo_usuario, tipoRol),
      };

      return { tipoRol, detallesRol: [detalle] };
    });

  if (roles.length > 0) return roles;
  return [{ tipoRol: "USER", detallesRol: [] }];
}

function normalizeSsoUserForUsage(user, sourceList) {
  if (!user || typeof user !== "object") return null;

  const identificacion = pickText(user.identificacion, user.cedula);
  if (!identificacion) return null;

  return {
    codigoErp: pickText(user.codigoErp, user.codigo_erp),
    tipoIdentificacion: pickText(
      user.tipoIdentificacion,
      user.tipo_identificacion,
      "CEDULA",
    ),
    identificacion,
    nombres: pickText(user.nombres),
    apellidos: pickText(user.apellidos),
    numeroCelular: pickText(user.numeroCelular, user.numero_celular),
    emailInstitucional: pickText(
      user.emailInstitucional,
      user.correo_institucional,
      user.email,
    ),
    emailPersonal: pickText(user.emailPersonal, user.correo_personal),
    sexo: pickText(user.sexo),
    rolesUsuario: normalizeRolesUsuarioFromSso(user, sourceList),
  };
}

function logUsoRecurso(apiBase, recursoId) {
  const url = `${apiBase}/log-uso/registrar/${recursoId}`;

  const esInvitado = getAuthValue("guest") === "true";
  const esAnonimo = getAuthValue("anonimo") === "true";
  const token = getAuthValue("token");
  const userSsoRaw = getAuthValue("user_sso");
  const userSsoRolesRaw = getAuthValue("user_sso_roles");
  const userSso = userSsoRaw ? safeParse(userSsoRaw) : null;
  const userSsoRoles = userSsoRolesRaw ? safeParse(userSsoRolesRaw) : null;
  const userPrincipal =
    Array.isArray(userSsoRoles) && userSsoRoles.length > 0 ? userSsoRoles[0] : userSso;
  const userNormalizado = normalizeSsoUserForUsage(userPrincipal, userSsoRoles);

  console.debug("Log de uso - snapshot:", {
    esInvitado,
    esAnonimo,
    token: !!token,
    userSso: userPrincipal,
    userNormalizado,
  });

  let baseUser;
  if (esInvitado) {
    console.debug("Construyendo payload para usuario INVITADO");
    baseUser = buildGuestUser(getAuthValue("guest_code"));
  } else if (esAnonimo) {
    console.debug("Construyendo payload para usuario ANÓNIMO");
    baseUser = buildAnonUser();
  } else if (userNormalizado) {
    console.debug("Construyendo payload para usuario SSO");
    baseUser = userNormalizado;
  } else {
    console.debug("Construyendo payload para usuario ANÓNIMO (fallback)");
    baseUser = buildAnonUser();
  }

  const payloads = buildUsagePayloadsByRole(baseUser);
  console.debug("Payloads generados:", payloads);

  const headers = { "Content-Type": "application/json" };
  if (token && !esInvitado && !esAnonimo) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Enviar N logs (uno por cada payload)
  if (navigator.sendBeacon && !headers.Authorization) {
    for (const p of payloads) {
      const blob = new Blob([JSON.stringify(p)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    }
    return;
  }

  // Fallback: secuencial para no saturar
  (async () => {
    for (const p of payloads) {
      try {
        console.debug("Enviando log de uso con payload:", p);
        await fetch(url, {
          method: "POST",
          headers,
          body: JSON.stringify(p),
          keepalive: true,
        });
      } catch { }
    }
  })();
}

function safeParse(str) {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function PantallaErrorInvitado({ motivo }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white shadow p-8 rounded max-w-md text-center">
        <h1 className="text-xl font-bold text-red-600 mb-2">
          Invitación inválida
        </h1>
        <p className="text-gray-700">
          {motivo || "La invitación ha expirado o fue revocada."}
        </p>
      </div>
    </div>
  );
}

/* =========================
   MODAL DE INFORMACIÓN (NUEVO)
   ========================= */

function InfoModal({ recurso, imagenSrc, onClose }) {
  const [imgOk, setImgOk] = React.useState(true);
  const [imgLoaded, setImgLoaded] = React.useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!recurso) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white/95 border border-gray-200 rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header / Close */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3
            id="modal-title"
            className="text-base md:text-lg font-semibold truncate"
          >
            {recurso.nombre}
          </h3>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border hover:bg-gray-100"
            aria-label="Cerrar"
            title="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Imagen en object-contain */}
        <div className="bg-gray-50">
          {!imgLoaded && (
            <div className="w-full max-h-[60vh] h-[220px] animate-pulse bg-gray-200" />
          )}
          {imgOk ? (
            <a
              href={buildRecursoGoUrl(API_BASE, recurso.id)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                registrarRecursoReciente(recurso);
                logUsoRecurso(API_BASE, recurso.id);
              }}
              className="block cursor-pointer"
              title="Abrir recurso"
            >
              <img
                src={imagenSrc || "/placeholder.png"}
                alt={recurso.nombre}
                className={`w-full object-contain mx-auto ${imgLoaded ? "block" : "hidden"}`}
                style={{ maxHeight: "60vh" }}
                onLoad={() => setImgLoaded(true)}
                onError={() => {
                  setImgOk(false);
                  setImgLoaded(true);
                }}
              />
            </a>
          ) : (
            <div className="w-full max-h-[60vh] h-[220px] flex items-center justify-center text-gray-500">
              Sin imagen disponible
            </div>
          )}
        </div>
        {/* Cuerpo */}
        <div className="px-5 pt-4 pb-5">
          <div className="flex flex-wrap gap-2 mb-3">
            {recurso?.categoria?.nombre && (
              <span className="px-2.5 py-1 text-xs rounded-full bg-rose-100 text-rose-700 border border-rose-200">
                {recurso.categoria.nombre}
              </span>
            )}
            {Array.isArray(recurso?.areas) &&
              recurso.areas.map((a) => (
                <span
                  key={a.id || a.nombre}
                  className="px-2.5 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200"
                >
                  {a.nombre}
                </span>
              ))}
          </div>

          <TutorialLink
            recurso={recurso}
            className="mb-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 text-sm font-semibold"
          />

          {/* Descripción HTML segura */}
          <div
            className="prose prose-sm max-w-none text-gray-800 leading-relaxed
                      [&_h4]:text-base [&_h4]:font-semibold [&_h4]:mt-2 [&_h4]:mb-1
                      [&_h5]:text-sm  [&_h5]:font-semibold [&_h5]:mt-2 [&_h5]:mb-1
                      [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5
                      [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600
                      max-h-[28vh] overflow-auto pr-1"
            style={{ scrollbarWidth: "thin" }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(recurso.descripcion || "", {
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />
        </div>
      </div>
    </div>
  );
}

function LoadingIndicator({ message = "Cargando..." }) {
  return (
    <div className="text-center py-12">
      <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      <p className="mt-4 text-gray-500">{message}</p>
    </div>
  );
}

/* =========================
   HOME
   ========================= */

function HomePage({ isAnon }) {
  const navigate = useNavigate();
  const [categorias, setCategorias] = useState([]);
  const [recursosPorCategoria, setRecursosPorCategoria] = useState({});
  const [imagenes, setImagenes] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 768 : true,
  );
  const [noticiasOpen, setNoticiasOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth >= 1024 : true,
  );
  const [mostrarAreas, setMostrarAreas] = useState(true);
  const [recursoSeleccionado, setRecursoSeleccionado] = useState(null);
  const [idsNuevos, setIdsNuevos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [filtroAreas, setFiltroAreas] = useState([]);
  const [filtroCategorias, setFiltroCategorias] = useState([]);
  const [mostrarCategorias, setMostrarCategorias] = useState(true);
  const [favoritos, setFavoritos] = useState([]);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [loading, setLoading] = useState(true);

  // Spotlight (hero) estilo Microsoft Store
  const [spotlight, setSpotlight] = useState([]);
  const [spotlightIndex, setSpotlightIndex] = useState(0);

  const [scrollActivo, setScrollActivo] = useState({});

  const buttonRef = useRef(null);

  const offsetTop =
    HEADER_BASE_HEIGHT + (MODO_DESARROLLO ? DEV_BANNER_HEIGHT : 0);

  const verificarScroll = useCallback((id) => {
    const el = document.getElementById(`scroll-${id}`);
    if (!el) return;

    const tieneOverflow = el.scrollWidth > el.clientWidth;

    setScrollActivo((prev) => {
      if (prev[id] === tieneOverflow) return prev;
      return { ...prev, [id]: tieneOverflow };
    });
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  const handleToggleNoticias = useCallback(() => {
    setNoticiasOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    const handleViewport = () => {
      if (window.innerWidth < 768) setSidebarOpen(false);
      if (window.innerWidth < 1024) setNoticiasOpen(false);
    };

    handleViewport();
    window.addEventListener("resize", handleViewport);
    return () => window.removeEventListener("resize", handleViewport);
  }, []);

  useEffect(() => {
    const esInvitado = getAuthValue("guest") === "true";
    const esAnonimo = getAuthValue("anonimo") === "true";

    if (esInvitado || esAnonimo) {
      localStorage.removeItem("favoritos");
    }
  }, []);

  useEffect(() => {
    const headerEl =
      document.querySelector("header") ||
      document.querySelector(".app-header") ||
      document.querySelector("#app-header");

    if (!headerEl) return;
    const prevTop = headerEl.style.top || "";
    headerEl.style.top = MODO_DESARROLLO
      ? `${DEV_BANNER_HEIGHT}px`
      : prevTop || "0px";
    return () => {
      headerEl.style.top = prevTop;
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Si es anónimo o invitado, pedimos solo recursos visibles para acceso público
        const esInvitado = getAuthValue("guest") === "true";
        const esAnonimo = getAuthValue("anonimo") === "true";
        const modoLimitado = isAnon || esAnonimo;

        const recursosUrl = modoLimitado
          ? `${API_BASE}/recursos?anon=true`
          : `${API_BASE}/recursos`;

        const [catRes, recRes] = await Promise.all([
          fetch(`${API_BASE}/categorias`),
          fetch(recursosUrl),
        ]);

        const categoriasData = await catRes.json();
        const recursosData = await recRes.json();

        // Filtro local por seguridad (el backend ya filtra INACTIVO en /anon)
        let activos = recursosData.filter((r) => r.estado !== "INACTIVO");

        const recursosOrdenadosPorFecha = [...activos].sort(
          (a, b) => new Date(b.fecha) - new Date(a.fecha),
        );
        const nuevos = recursosOrdenadosPorFecha.slice(0, 3).map((r) => r.id);
        setIdsNuevos(nuevos);

        // Hero: tomamos los más recientes como destacados
        setSpotlight(recursosOrdenadosPorFecha.slice(0, 5));
        setSpotlightIndex(0);

        const agrupados = {};
        for (const cat of categoriasData) {
          const recursosCat = activos.filter(
            (r) => r.categoria?.nombre === cat.nombre,
          );
          agrupados[cat.nombre] = recursosCat;
        }

        const categoriasOrdenadas = categoriasData.sort(
          (a, b) => (a.orden ?? 0) - (b.orden ?? 0),
        );

        const todasLasAreas = activos.flatMap((r) => r.areas || []);
        const nombresUnicos = Array.from(
          new Set(todasLasAreas.map((a) => a.nombre)),
        );

        setAreas(nombresUnicos);
        setCategorias(categoriasOrdenadas);
        setRecursosPorCategoria(agrupados);
      } catch (error) {
        console.error("Error cargando datos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAnon]);

  useEffect(() => {
    if (!Array.isArray(spotlight) || spotlight.length === 0) return;

    let cancelled = false;
    const topIds = spotlight.slice(0, 4).map((r) => r.id).filter(Boolean);

    Promise.all(topIds.map((id) => fetchResourceImage(id))).then((sources) => {
      if (cancelled) return;
      setImagenes((prev) => {
        const next = { ...prev };
        topIds.forEach((id, idx) => {
          const src = sources[idx];
          if (isDataImageUrl(src)) next[id] = src;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [spotlight]);

  useEffect(() => {
    if (!Array.isArray(spotlight) || spotlight.length === 0) return;

    let cancelled = false;
    const current = spotlight[Math.max(0, Math.min(spotlightIndex, spotlight.length - 1))];
    const sideOffsets = [1, 2, 3].filter((o) => o < spotlight.length);
    const ids = [current?.id, ...sideOffsets.map((o) => spotlight[(spotlightIndex + o) % spotlight.length]?.id)]
      .filter(Boolean);
    const uniqueIds = [...new Set(ids)];

    if (uniqueIds.length === 0) return;

    Promise.all(uniqueIds.map((id) => fetchResourceImage(id))).then((sources) => {
      if (cancelled) return;
      setImagenes((prev) => {
        const next = { ...prev };
        uniqueIds.forEach((id, idx) => {
          const src = sources[idx];
          if (isDataImageUrl(src)) next[id] = src;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [spotlight, spotlightIndex]);

  useEffect(() => {
    if (!recursoSeleccionado?.id) return;
    let cancelled = false;

    fetchResourceImage(recursoSeleccionado.id).then((src) => {
      if (cancelled) return;
      setImagenes((prev) => ({ ...prev, [recursoSeleccionado.id]: src }));
    });

    return () => {
      cancelled = true;
    };
  }, [recursoSeleccionado]);

  // Auto-rotación del spotlight (suave)
  useEffect(() => {
    if (!Array.isArray(spotlight) || spotlight.length <= 1) return;
    const t = setInterval(() => {
      setSpotlightIndex((i) => (i + 1) % spotlight.length);
    }, 6500);
    return () => clearInterval(t);
  }, [spotlight]);

  // Si cambian los datos y el índice queda fuera, lo reajustamos
  useEffect(() => {
    if (!Array.isArray(spotlight) || spotlight.length === 0) return;
    if (spotlightIndex >= spotlight.length) setSpotlightIndex(0);
  }, [spotlight, spotlightIndex]);

  const postLog = async (url, payload, retries = 2) => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        await axios.post(url, payload);
        return;
      } catch (err) {
        if (attempt === retries) throw err;
        await new Promise((res) => setTimeout(res, 400 * (attempt + 1)));
      }
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        await new Promise((res) => setTimeout(res, 100));

        const esInvitado = getAuthValue("guest") === "true";
        const esAnonimo = getAuthValue("anonimo") === "true";
        const guestCode = getAuthValue("guest_code") || null;

        console.log("📋 Estado actual:", { esInvitado, esAnonimo, guestCode });

        const GUEST_LOG_KEY = "guest_log_sent";
        const ANON_LOG_KEY = "anon_log_sent";
        const AUTH_LOG_KEY = "auth_log_sent";

        if (window.__logInProgress) return;
        window.__logInProgress = true;

        /* =========================
          INVITADO
          ========================= */

        if (esInvitado && guestCode) {
          if (sessionStorage.getItem(GUEST_LOG_KEY) === "1") {
            console.log("ℹ️ Log INVITADO ya registrado, omitiendo.");
            window.__logInProgress = false;
            return;
          }

          const guestUser = buildGuestUser(guestCode);
          try {
            sessionStorage.setItem(GUEST_LOG_KEY, "1");
            await postLog(`${API_BASE}/logs/registro`, guestUser);
            console.log("✅ Log de sesión INVITADO registrado:", guestUser);
            setAuthValueLocal("anonimo", "false");
            window.dispatchEvent(new Event("auth_changed"));
          } catch (err) {
            console.error("❌ Error al registrar log INVITADO:", err);
            sessionStorage.removeItem(GUEST_LOG_KEY);
          }
          window.__logInProgress = false;
          return;
        }

        /* =========================
          ANÓNIMO
          ========================= */

        if (esAnonimo || MODO_DESARROLLO) {
          if (sessionStorage.getItem(ANON_LOG_KEY) === "1") {
            console.log("ℹ️ Log ANÓNIMO ya registrado, omitiendo.");
            window.__logInProgress = false;
            return;
          }

          const anonUser = buildAnonUser();
          try {
            sessionStorage.setItem(ANON_LOG_KEY, "1");
            await postLog(`${API_BASE}/logs/registro`, anonUser);
            console.log("✅ Log de sesión ANÓNIMO registrado:", anonUser);
            setAuthValueLocal("guest", "false");
            window.dispatchEvent(new Event("auth_changed"));
          } catch (err) {
            console.error("❌ Error al registrar log ANÓNIMO:", err);
            sessionStorage.removeItem(ANON_LOG_KEY);
          }
          window.__logInProgress = false;
          return;
        }

        /* =========================
          AUTENTICADO (SSO)
          ========================= */

        if (sessionStorage.getItem(AUTH_LOG_KEY) === "1") {
          console.log("ℹ️ Log autenticado ya registrado, omitiendo.");
          window.__logInProgress = false;
          return;
        }

        try {
          const token = getAuthValue("token");

          console.log("🔐 Obteniendo datos del usuario autenticado SSO...");
          const response = await axios.post(
            `${API_BASE}/sso/funciones`,
            {},
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
          );

          const userDTOArray = Array.isArray(response.data) ? response.data : [];
          console.log("👤 Datos del usuario autenticado obtenidos:", userDTOArray);

          // Prioriza qué "cargo" usar como perfil principal
          const cargoPriority = ["TÉCNICO", "DOCENTE", "ADMINISTRATIVO", "ALUMNO"];

          const userDTO =
            userDTOArray
              .slice()
              .sort(
                (a, b) =>
                  cargoPriority.indexOf(a?.cargo ?? "") -
                  cargoPriority.indexOf(b?.cargo ?? "")
              )[0] ?? null;

          const userNormalizado = normalizeSsoUserForUsage(userDTO, userDTOArray);
          const perfilesNormalizados = userDTOArray
            .map((u) => normalizeSsoUserForUsage(u, [u]))
            .filter(Boolean);

          if (userNormalizado?.identificacion) {
            try {
              await postLog(`${API_BASE}/logs/registro`, userNormalizado);
              sessionStorage.setItem(AUTH_LOG_KEY, "1");

              // Guarda ambos: el principal y todos los perfiles normalizados
              setAuthValueLocal("user_sso", JSON.stringify(userNormalizado));
              setAuthValueLocal("user_sso_roles", JSON.stringify(perfilesNormalizados));

              setAuthValueLocal("anonimo", "false");
              window.dispatchEvent(new Event("auth_changed"));

              console.log("Log de sesión AUTENTICADO registrado:", userNormalizado);
            } catch (logErr) {
              sessionStorage.removeItem(AUTH_LOG_KEY);
              throw logErr;
            }
          }
        } catch (err) {
          console.error("Error al obtener o registrar usuario autenticado:", err);
        } finally {
          window.__logInProgress = false;
        }
      } catch (error) {
        console.error("Error general en fetchUserData:", error);
        window.__logInProgress = false;
      }
    };

    fetchUserData();
  }, [isAnon]);

  useEffect(() => {
    const refreshBibliotecaPersonal = () => {
      const esInvitado = getAuthValue("guest") === "true";
      const esAnonimo = getAuthValue("anonimo") === "true";

      if (esInvitado || esAnonimo) {
        localStorage.removeItem("favoritos");
      }

      const esAutenticado =
        !esInvitado && !esAnonimo && !!getAuthValue("token");
      const storage = esAutenticado ? localStorage : sessionStorage;
      const favsRaw = JSON.parse(storage.getItem("favoritos") || "[]");
      const esAdmin = localStorage.getItem("rol") === "LIBRARIAN" || localStorage.getItem("rol") === "ADMIN";
      const favs = esAdmin ? favsRaw : (Array.isArray(favsRaw) ? favsRaw.filter((r) => r.estado !== "INACTIVO") : []);
      setFavoritos(favs);
      try {
        storage.setItem("favoritos", JSON.stringify(favs));
      } catch (e) {
        // ignore
      }
      console.debug("Es autenticado:", esAutenticado);
      if (!esAutenticado) return;
      console.debug("Sincronizando biblioteca personal con DB para usuario autenticado...");

      const token = getAuthValue("token");
      const recientesLocal = JSON.parse(localStorage.getItem("recientes") || "[]");

      const syncBibliotecaPersonal = async () => {
        try {
          await Promise.all([
            fetch(`${API_BASE}/mi-biblioteca/favoritos/merge`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ recursoIds: favs.map((r) => r.id) }),
            }),
            fetch(`${API_BASE}/mi-biblioteca/recientes/merge`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ recursoIds: recientesLocal.map((r) => r.id) }),
            }),
          ]);

          const [favResp, recResp] = await Promise.all([
            fetch(`${API_BASE}/mi-biblioteca/favoritos`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_BASE}/mi-biblioteca/recientes?limit=20`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

          if (favResp.ok) {
            const favoritosDb = await favResp.json();
            localStorage.setItem("favoritos", JSON.stringify(favoritosDb));
            setFavoritos(favoritosDb);
            emitFavoritosActualizados();
          }

          if (recResp.ok) {
            const recientesDb = await recResp.json();
            localStorage.setItem("recientes", JSON.stringify(recientesDb));
            window.dispatchEvent(new Event("recientes-actualizados"));
          }
        } catch (err) {
          console.warn("No se pudo sincronizar favoritos/recientes con DB:", err);
        }
      };

      syncBibliotecaPersonal();
    };

    refreshBibliotecaPersonal();
    window.addEventListener("auth_changed", refreshBibliotecaPersonal);
    window.addEventListener("storage", refreshBibliotecaPersonal);
    return () => {
      window.removeEventListener("auth_changed", refreshBibliotecaPersonal);
      window.removeEventListener("storage", refreshBibliotecaPersonal);
    };
  }, []);

  useEffect(() => {
    categorias.forEach((cat) => {
      const el = document.getElementById(`scroll-${cat.id}`);
      if (!el) return;

      const tieneOverflow = el.scrollWidth > el.clientWidth;

      setScrollActivo((prev) => {
        if (prev[cat.id] === tieneOverflow) return prev;
        return { ...prev, [cat.id]: tieneOverflow };
      });
    });
  }, [categorias, recursosPorCategoria, filtroAreas, filtroCategorias]);

  // hacer que scroll-favs se comporte igual en el overflow que las categorías
  useEffect(() => {
    const el = document.getElementById(`scroll-favs`);
    if (!el) return;
    const tieneOverflow = el.scrollWidth > el.clientWidth;
    setScrollActivo((prev) => {
      if (prev["favs"] === tieneOverflow) return prev;
      return { ...prev, favs: tieneOverflow };
    });
  }, [favoritos]);

  useEffect(() => {
    const handleResize = () => {
      categorias.forEach((cat) => verificarScroll(cat.id));
      verificarScroll("favs");
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [categorias, verificarScroll]);

  const toggleFavorito = useCallback((recurso) => {
    const esInvitado = getAuthValue("guest") === "true";
    const esAnonimo = getAuthValue("anonimo") === "true";
    const esAutenticado =
      !esInvitado && !esAnonimo && !!getAuthValue("token");
    const storage = esAutenticado ? localStorage : sessionStorage;

    let favoritosActuales = JSON.parse(storage.getItem("favoritos")) || [];
    const existe = favoritosActuales.some((r) => r.id === recurso.id);

    if (existe) {
      favoritosActuales = favoritosActuales.filter((r) => r.id !== recurso.id);
    } else {
      favoritosActuales.push(recurso);
    }

    storage.setItem("favoritos", JSON.stringify(favoritosActuales));
    setFavoritos(favoritosActuales);
    emitFavoritosActualizados();

    if (esAutenticado) {
      const token = getAuthValue("token");
      const method = existe ? "DELETE" : "POST";
      fetch(`${API_BASE}/mi-biblioteca/favoritos/${recurso.id}`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      }).catch((err) =>
        console.warn("No se pudo sincronizar favorito en backend:", err)
      );
    }
  }, []);

  // Normaliza la imagen (algunas respuestas previas podían venir como array)
  const imgSrc = useCallback((id) => {
    const v = imagenes?.[id] ?? resourceImageCache.get(id);
    if (Array.isArray(v)) return v[0] || FALLBACK_RESOURCE_IMAGE;
    return v || FALLBACK_RESOURCE_IMAGE;
  }, [imagenes]);

  const stickyTop = offsetTop;

  const handleInfo = useCallback((recurso) => {
    setRecursoSeleccionado(recurso);
  }, []);

  const handleOpen = useCallback((recurso) => {
    registrarRecursoReciente(recurso);
    logUsoRecurso(API_BASE, recurso.id);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <DevBanner visible={MODO_DESARROLLO} height={DEV_BANNER_HEIGHT} />
      <AppHeader
        onToggleSidebar={handleToggleSidebar}
        onToggleNoticias={handleToggleNoticias}
        noticiasOpen={noticiasOpen}
        buildEzproxyLoginUrl={buildEzproxyLoginUrl}
        clearAuthAll={clearAuthAll}
        getAuthSnapshot={getAuthSnapshot}
        modoDesarrollo={MODO_DESARROLLO}
        devBannerHeight={DEV_BANNER_HEIGHT}
        headerBaseHeight={HEADER_BASE_HEIGHT}
        buttonRef={buttonRef}
      />
      <div
        className={`sticky z-30 transition-all duration-300
            ${sidebarOpen ? "md:ml-64" : "ml-0"}
            ${noticiasOpen ? "lg:mr-80" : "mr-0"}
          `}
        style={{ top: `${stickyTop}px` }}
      >
        {/* =========================
              BANNERS CONTROLADOS POR URL
            ========================= */}

        {(window.location.pathname === "/anon" || getAuthValue("anonimo") === "true") && (
          <div className="bg-yellow-100 text-yellow-800 border-l-4 border-yellow-500 rounded-b p-3 md:p-4 shadow-sm">
            <p className="text-sm md:text-base leading-snug">
              Ha accedido a la Biblioteca de forma <strong>anónima</strong> y
              únicamente cuenta con recursos públicos. Para acceder a los
              recursos institucionales,&nbsp;
              <a
                href={buildEzproxyLoginUrl(`${window.location.pathname}${window.location.search}`)}
                className="underline text-blue-700 hover:text-blue-900"
                target="_blank"
                rel="noreferrer"
              >
                inicie sesión ahora
              </a>
              .
            </p>
          </div>
        )}

        {(new URLSearchParams(window.location.search).has("inv") || getAuthValue("guest") === "true") && (
          <div className="bg-blue-100 text-blue-800 border-l-4 border-blue-500 rounded-b p-3 md:p-4 shadow-sm">
            <p className="text-sm md:text-base leading-snug">
              Ha accedido como <strong>INVITADO</strong>. El acceso es temporal
              y puede estar limitado.
            </p>
          </div>
        )}

        <div className="md:hidden bg-gray-100 p-3">
          <Metabuscador />
        </div>
      </div>
      <div className="flex flex-1 relative">
        <div style={{ paddingTop: `${offsetTop}px` }}>
          <Sidebar isOpen={sidebarOpen} offsetTop={offsetTop} />
        </div>

        <main
          className={`flex-1 p-4 sm:p-5 lg:p-6 overflow-auto transition-all duration-500 ease-in-out ${sidebarOpen ? "md:ml-64" : "ml-0"} ${noticiasOpen ? "lg:mr-80" : "mr-0"}`}
          style={{ paddingTop: `${offsetTop}px` }}
        >
          {loading ? (
            <LoadingIndicator message="Cargando recursos..." />
          ) : (
            <>
              {/* =========================
                SPOTLIGHT (inspirado en Microsoft Store)
              ========================= */}
              <div id="secciones" />
              <SpotlightHero
                items={spotlight}
                index={spotlightIndex}
                setIndex={setSpotlightIndex}
                getImgSrc={imgSrc}
                apiBase={API_BASE}
                favoritos={favoritos}
                onToggleFav={toggleFavorito}
                onInfo={handleInfo}
                onOpen={handleOpen}
              />{" "}
              {/* FILTROS (estilo Store) */}
              <div className="mb-8 rounded-3xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                <div
                  className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 cursor-pointer select-none"
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                >
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-gray-900 text-white">
                    <LayoutGrid className="w-5 h-5" />
                  </span>

                  <div className="leading-tight flex-1">
                    <div className="text-sm font-semibold text-gray-900">
                      Filtros
                    </div>
                    <div className="text-xs text-gray-500">
                      Afina la búsqueda por áreas y categorías
                    </div>
                  </div>

                  {/* Indicador visual */}
                  <span className="text-gray-500 text-sm">
                    {mostrarFiltros ? "▾" : "▸"}
                  </span>
                </div>
                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${mostrarFiltros
                    ? "max-h-[2000px] opacity-100"
                    : "max-h-0 opacity-0"
                    }`}
                >
                  <div className="p-5">
                    <div className="mb-6">
                      <h3
                        className="text-xl font-bold mb-2 cursor-pointer select-none"
                        onClick={() => setMostrarAreas(!mostrarAreas)}
                      >
                        » Filtrar por área {mostrarAreas ? "▾" : "▸"}
                      </h3>

                      <div
                        className={`transition-all duration-300 ease-in-out overflow-hidden ${mostrarAreas
                          ? "max-h-[500px] opacity-100"
                          : "max-h-0 opacity-0"
                          }`}
                      >
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setFiltroAreas([])}
                            className={`px-3 py-1 rounded ${filtroAreas.length === 0
                              ? "bg-red-500 text-white"
                              : "bg-white border border-red-500 hover:bg-red-100"
                              }`}
                          >
                            Todos
                          </button>
                          {areas.map((a) => {
                            const activo = filtroAreas.includes(a);
                            return (
                              <button
                                key={a}
                                onClick={() => {
                                  if (activo) {
                                    setFiltroAreas(
                                      filtroAreas.filter((area) => area !== a),
                                    );
                                  } else {
                                    setFiltroAreas([...filtroAreas, a]);
                                  }
                                }}
                                className={`px-3 py-1 rounded border transition-all duration-300 transform ${activo
                                  ? "bg-red-500 text-white scale-105"
                                  : "bg-white text-gray-800 border-red-500 hover:bg-red-100"
                                  }`}
                              >
                                {a}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    {/* FILTRO POR CATEGORÍA */}
                    <div className="mb-6">
                      <h3
                        className="text-xl font-bold mb-2 cursor-pointer select-none"
                        onClick={() => setMostrarCategorias(!mostrarCategorias)}
                      >
                        » Filtrar por categoría {mostrarCategorias ? "▾" : "▸"}
                      </h3>
                      <div
                        className={`flex flex-wrap gap-2 overflow-hidden transition-all duration-500 ease-in-out ${mostrarCategorias
                          ? "max-h-[300px] opacity-100"
                          : "max-h-0 opacity-0"
                          }`}
                      >
                        <button
                          onClick={() => setFiltroCategorias([])}
                          className={`px-3 py-1 rounded ${filtroCategorias.length === 0 ? "bg-red-500 text-white" : "bg-white border border-red-500 hover:bg-red-100"}`}
                        >
                          Todos
                        </button>
                        {categorias.map((c) => {
                          const activo = filtroCategorias.includes(c.nombre);
                          return (
                            <button
                              key={c.id}
                              onClick={() => {
                                if (activo) {
                                  setFiltroCategorias(
                                    filtroCategorias.filter(
                                      (cat) => cat !== c.nombre,
                                    ),
                                  );
                                } else {
                                  setFiltroCategorias([
                                    ...filtroCategorias,
                                    c.nombre,
                                  ]);
                                }
                              }}
                              className={`px-3 py-1 rounded border transition-all duration-300 transform ${activo ? "bg-red-500 text-white scale-105" : "bg-white text-gray-800 border-red-500 hover:bg-red-100"}`}
                            >
                              {c.nombre}
                            </button>
                          );
                        })}
                      </div>
                    </div>{" "}
                  </div>
                </div>
              </div>{" "}
              {/* RECURSOS FILTRADOS */}
              {/* Favoritos */}
              <div id="favoritos" />
              {Array.isArray(favoritos) && favoritos.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                      Favoritos
                    </h2>
                    <span className="text-sm text-gray-500">
                      {favoritos.length} recurso(s)
                    </span>
                  </div>

                  <div className="flex items-center gap-1">
                    {scrollActivo.favs && (
                        <button
                          className="bg-white p-2 rounded-full shadow hover:bg-gray-200 flex-shrink-0"
                          onClick={() =>
                            (document.getElementById(`scroll-favs`).scrollLeft -= 200)
                          }
                          aria-label="Desplazar a la izquierda"
                          title="Desplazar a la izquierda"
                        >
                          <ChevronLeft />
                        </button>
                    )}

                    <div
                      id="scroll-favs"
                      ref={() => verificarScroll("favs")}
                      className="flex gap-4 overflow-x-auto pb-2 cursor-grab no-scrollbar scroll-smooth"
                      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                      {favoritos.map((recurso, i) => (
                        <div
                          key={`fav-${recurso.id}`}
                          className="relative min-w-[220px] flex-shrink-0 bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition overflow-hidden group animate-recurso"
                          style={{ animationDelay: `${i * 40}ms` }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorito(recurso);
                            }}
                            className="absolute top-3 right-3 text-red-500 hover:scale-110 transition-transform z-20"
                            title="Quitar de favoritos"
                          >
                            ❤️
                          </button>

                          <LazyResourceImage
                            resourceId={recurso.id}
                            alt={recurso.nombre}
                            className="w-full h-36 object-cover"
                          />

                          <div className="px-4 pb-4 pt-3">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                              {recurso.nombre}
                            </p>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {recurso?.categoria?.nombre || "Recurso"}
                              </p>
                            </div>

                          <TutorialLink
                            recurso={recurso}
                            className="absolute bottom-3 right-14 z-20 inline-flex items-center justify-center w-9 h-9 rounded-full border shadow-sm bg-white/90 text-gray-700 hover:bg-white [&_span]:sr-only"
                          />

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRecursoSeleccionado(recurso);
                            }}
                            className="absolute bottom-3 right-3 z-20 inline-flex items-center justify-center w-9 h-9 rounded-full border shadow-sm bg-white/90 text-gray-600 hover:bg-white"
                            title="Información"
                          >
                            <Info className="w-4 h-4" />
                          </button>

                          <a
                            href={buildRecursoGoUrl(API_BASE, recurso.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              registrarRecursoReciente(recurso);
                              logUsoRecurso(API_BASE, recurso.id);
                            }}
                            className="absolute inset-0"
                          >
                            <span className="sr-only">Abrir {recurso.nombre}</span>
                          </a>
                        </div>
                      ))}
                    </div>
                    {scrollActivo.favs && (
                      <button
                        className="bg-white p-2 rounded-full shadow hover:bg-gray-200 flex-shrink-0"
                        onClick={() =>
                          (document.getElementById(`scroll-favs`).scrollLeft += 200)
                        }
                        aria-label="Desplazar a la derecha"
                        title="Desplazar a la derecha"
                      >
                        <ChevronRight />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {categorias.map((cat) => {
                const recursos = recursosPorCategoria[cat.nombre];
                if (!recursos || recursos.length === 0) return null;

                const filtrados = recursos.filter(
                  (r) =>
                    (filtroAreas.length === 0 ||
                      r.areas?.some((a) => filtroAreas.includes(a.nombre))) &&
                    (filtroCategorias.length === 0 ||
                      filtroCategorias.includes(cat.nombre)),
                );
                if (filtrados.length === 0) return null;

                return (
                  <div key={cat.id} className="mb-10">
                    <div className="flex items-center justify-between mb-3">
                      <h2 className="text-xl md:text-2xl font-semibold text-gray-900">
                        {cat.nombre.replace(/_/g, " ")}
                      </h2>
                      <button
                        className="inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900"
                        onClick={() => navigate(`/categoria/${encodeURIComponent(cat.nombre)}`)}
                        title="Ver todo"
                      >
                        Ver todo <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      {scrollActivo[cat.id] && (
                        <button
                          className="bg-white p-2 rounded-full shadow hover:bg-gray-200 flex-shrink-0"
                          onClick={() =>
                            (document.getElementById(`scroll-${cat.id}`).scrollLeft -= 200)
                          }
                        >
                          <ChevronLeft />
                        </button>
                      )}
                      <div
                        id={`scroll-${cat.id}`}
                        ref={() => verificarScroll(cat.id)}
                        className="flex gap-4 overflow-x-scroll pb-2 cursor-grab no-scrollbar scroll-smooth"
                        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                        onLoad={() => verificarScroll(cat.id)}
                        onMouseDown={(e) => {
                          const el = e.currentTarget;
                          let startX = e.pageX - el.offsetLeft;
                          let scrollLeft = el.scrollLeft;
                          const onMouseMove = (e2) => {
                            el.scrollLeft =
                              scrollLeft - (e2.pageX - el.offsetLeft - startX);
                          };
                          const onMouseUp = () => {
                            window.removeEventListener("mousemove", onMouseMove);
                            window.removeEventListener("mouseup", onMouseUp);
                          };
                          window.addEventListener("mousemove", onMouseMove);
                          window.addEventListener("mouseup", onMouseUp);
                        }}
                      >
                        {filtrados.map((recurso, i) => (
                          <div
                            key={recurso.id}
                            className="relative min-w-[220px] flex-shrink-0 bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-md transition overflow-hidden group animate-recurso"
                            style={{ animationDelay: `${i * 60}ms` }}
                          >
                            {idsNuevos.includes(recurso.id) && (
                              <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] px-2 py-[2px] rounded-full text-xs font-bold shadow z-30">
                                NUEVO
                              </span>
                            )}

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorito(recurso);
                              }}
                              className="absolute top-2 right-2 text-red-500 hover:scale-110 transition-transform z-20"
                              title="Marcar como favorito"
                            >
                              {favoritos.some((r) => r.id === recurso.id)
                                ? "❤️"
                                : "🤍"}
                            </button>

                            <LazyResourceImage
                              resourceId={recurso.id}
                              alt={recurso.nombre}
                              className="w-full h-36 object-cover"
                            />
                            <div className="px-4 pb-4 pt-3">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {recurso.nombre}
                              </p>
                              <p className="text-xs text-gray-500 mt-1 truncate">
                                {recurso?.categoria?.nombre || "Recurso"}
                            </p>
                          </div>

                            {/* BOTÓN INFO → abre modal */}

                            <TutorialLink
                              recurso={recurso}
                              className="absolute bottom-2 right-12 z-20 inline-flex items-center justify-center w-8 h-8 rounded-full shadow bg-white text-gray-700 hover:bg-gray-200 [&_span]:sr-only"
                            />

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setRecursoSeleccionado(recurso);
                              }}
                              className="absolute bottom-2 right-2 z-20 inline-flex items-center justify-center w-8 h-8 rounded-full shadow bg-white text-gray-600 hover:bg-gray-200"
                              title="Información"
                            >
                              <Info className="w-4 h-4" />
                            </button>

                            {/* ENLACE DE APERTURA DEL RECURSO + LOG */}
                            <a
                              href={buildRecursoGoUrl(API_BASE, recurso.id)} // 👈 SIEMPRE pasa por el backend
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => {
                                registrarRecursoReciente(recurso);
                                logUsoRecurso(API_BASE, recurso.id);
                              }}
                              className="absolute inset-0"
                            >
                              <span className="sr-only">
                                Abrir {recurso.nombre}
                              </span>
                            </a>
                          </div>
                        ))}
                      </div>
                      {scrollActivo[cat.id] && (
                        <button
                          className="bg-white p-2 rounded-full shadow hover:bg-gray-200 flex-shrink-0"
                          onClick={() =>
                            (document.getElementById(`scroll-${cat.id}`).scrollLeft += 200)
                          }
                        >
                          <ChevronRight />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Acerca de (ancla para header) */}
              <div id="acerca" className="mt-10">
                <div className="rounded-3xl bg-white border border-gray-200 shadow-sm p-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Acerca de Biblioteca Virtual
                  </h3>
                  <p className="text-sm text-gray-600 mt-2">
                    Explora recursos digitales organizados por categorías y áreas.
                    Usa los filtros para encontrar contenido más rápido y marca tus
                    favoritos para acceder a ellos cuando los necesites.
                  </p>
                </div>
              </div>
              {/* ========= MODAL DE INFORMACIÓN ========= */}
              {recursoSeleccionado && (
                <InfoModal
                  recurso={recursoSeleccionado}
                  imagenSrc={imgSrc(recursoSeleccionado.id)}
                  onClose={() => setRecursoSeleccionado(null)}
                />
              )}
            </>
          )}
        </main>

        <div style={{ paddingTop: `${offsetTop}px` }}>
          <NoticiasSidebar
            visible={noticiasOpen}
            setVisible={setNoticiasOpen}
            offsetTop={offsetTop}
            buttonRef={buttonRef}
          />
        </div>
      </div>
    </div>
  );
}

/* =========================
   RUTAS + CONTROL DE LOGIN
   ========================= */

function InicioNormal() {
  console.log("Renderizando HomePage en modo NORMAL");
  return <HomePage isAnon={false} />;
}
function InicioAnonimo() {
  console.log("Renderizando HomePage en modo ANÓNIMO");
  return <HomePage isAnon={true} />;
}

function RoutedApp() {
  const location = useLocation();
  const navigate = useNavigate();

  const [inviteStatus, setInviteStatus] = useState("ok"); // ok | loading | invalido
  const [motivo, setMotivo] = useState("");

  // Estado de procesamiento para compatibilidad con flujo EZproxy estilo PHP (?rq=...)
  const [ezStatus, setEzStatus] = useState("ok"); // ok | loading | error
  const [ezMotivo, setEzMotivo] = useState("");
  const ezRqLockRef = useRef(false);

  // Captura token devuelto por el callback SSO (ej: #token=...)
  useEffect(() => {
    const url = new URL(window.location.href);

    let token = url.searchParams.get("token");

    if (!token) {
      const hash = (url.hash || "").replace(/^#/, "");
      const hp = new URLSearchParams(hash);
      token = hp.get("token");
    }

    if (token) {
      clearAuthRuntimeFlags();
      setAuthValueLocal("token", token);
      setAuthValueLocal("guest", "false");
      setAuthValueLocal("anonimo", "false");
      setAuthValueLocal("guest_code", null);
      window.dispatchEvent(new Event("auth_changed"));

      // Limpia token de la URL
      url.searchParams.delete("token");
      url.hash = "";
      const qs = url.searchParams.toString();
      window.history.replaceState(null, "", url.pathname + (qs ? `?${qs}` : ""));
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    /* =========================
       EZPROXY LEGACY (index.php)
       - rq: sesión (ej. rq=0102628880, rq=invitado, rq=admin)
       - an: código anónimo (ej. an=HYu2364579 o an=1)
       ========================= */

    const rq = (params.get("rq") || "").trim();
    const an = (params.get("an") || "").trim();
    const ssoError = (params.get("sso_error") || "").trim();
    const bypassLogin = MODO_DESARROLLO || isDevHost();

    // rq=admin => redirección directa al panel (como en PHP)
    if (rq === "admin") {
      window.location.replace("https://vpn.ucacue.edu.ec/admin");
      return;
    }

    // rq=invitado => modo invitado simple (compat PHP)
    if (rq === "invitado" || rq === "guest") {
      // 1. Persistencia de datos
      setAuthValueLocal("guest", "true");
      setAuthValueLocal("guest_code", "invitado");
      setAuthValueLocal("anonimo", "false");

      // 2. Notificar a otros componentes (Header, Sidebars, etc.)
      window.dispatchEvent(new Event("auth_changed"));

      // 3. Limpieza de URL (Estilo "Silent Clean")
      const url = new URL(window.location.href);
      url.searchParams.delete("rq");
      const cleanPath = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") + url.hash;
      window.history.replaceState(null, "", cleanPath);

      // 4. Redirección interna de React para activar el renderizado
      // Esto asegura que el componente InicioNormal detecte el estado 'guest'
      navigate("/", { replace: true });
      return;
    }

    if (ssoError) {
      // Compatibilidad legacy: usuarios de EZproxy definidos en user.txt (invitado/guest)
      // pueden no tener correo institucional. En el sistema antiguo se los trataba como "invitado".
      if (ssoError === "no_email") {
        clearAuthRuntimeFlags();
        setAuthValueLocal("token", null);
        setAuthValueLocal("guest", "true");
        setAuthValueLocal("guest_code", "invitado");
        setAuthValueLocal("anonimo", "false");
        window.dispatchEvent(new Event("auth_changed"));

        const url = new URL(window.location.href);
        url.searchParams.delete("sso_error");
        const qs = url.searchParams.toString();
        window.history.replaceState(null, "", url.pathname + (qs ? `?${qs}` : "") + url.hash);
        navigate("/", { replace: true });
        return;
      }

      setEzStatus("error");
      setEzMotivo(`No se pudo iniciar sesion (${ssoError}).`);
      return;
    }

    /* =========================
       INVITADO (?inv=CODE) - flujo actual
       ========================= */

    const inv = params.get("inv");
    if (inv) {
      setInviteStatus("loading");

      fetch(`${API_BASE}/invitados/validar?code=${encodeURIComponent(inv)}`)
        .then((r) => r.json())
        .then((data) => {
          if (data?.valido && data?.jwt) {
            setAuthValueLocal("token", data.jwt);
            setAuthValueLocal("guest", "true");
            setAuthValueLocal("guest_code", inv);
            setAuthValueLocal("anonimo", "false");
            window.dispatchEvent(new Event("auth_changed"));
            setInviteStatus("ok");
          } else {
            // Limpiar datos
            clearAuthAll();
            setMotivo(data?.motivo || "Invitación inválida o expirada");
            setInviteStatus("invalido");
          }
        })
        .catch(() => {
          clearAuthAll();
          setMotivo("Error de conexión al servidor");
          setInviteStatus("invalido");
        });

      return;
    }

    /* =========================
       ANÓNIMO (?an=... o /anon) - compatible con PHP y React
       ========================= */

    const anonPorParam = isAnonParamValid(an);
    const anonPorRuta = location.pathname === "/anon";
    if (anonPorParam || anonPorRuta) {
      setAuthValueLocal("anonimo", "true");
      setAuthValueLocal("guest", "false");
      setAuthValueLocal("guest_code", null);
      window.dispatchEvent(new Event("auth_changed"));

      // Fuerza la ruta /anon para que el HomePage cargue recursos filtrados (anon=true)
      if (location.pathname !== "/anon") {
        navigate("/anon", { replace: true });
      }
      return;
    }

    /* =========================
       AUTO-ANÓNIMO (toggle tipo PHP)
       ========================= */

    const sinSesion = !hasValidSession();
    const esInvitado = getAuthValue("guest") === "true";
    const esAnonimo = getAuthValue("anonimo") === "true";

    if (!bypassLogin && sinSesion && !esInvitado && !esAnonimo && ACTIVAR_INGRESO_ANONIMO) {
      setAuthValueLocal("anonimo", "true");
      setAuthValueLocal("guest", "false");
      setAuthValueLocal("guest_code", null);
      window.dispatchEvent(new Event("auth_changed"));
      navigate("/anon", { replace: true });
      return;
    }

    /* =========================
       rq=... => intentar canjear rq por JWT (si el backend lo soporta)
       ========================= */

    if (rq) {
      // Si ya hay token, solo limpiamos rq de la URL y seguimos.
      if (getAuthValue("token")) {
        const url = new URL(window.location.href);
        url.searchParams.delete("rq");
        const qs = url.searchParams.toString();
        window.history.replaceState(null, "", url.pathname + (qs ? `?${qs}` : "") + url.hash);
        return;
      }

      if (!bypassLogin && sinSesion && !ezRqLockRef.current) {
        ezRqLockRef.current = true;
        setEzStatus("loading");
        setEzMotivo("");

        (async () => {
          // En lugar de "exchange" por API, delegamos al callback SSO del backend,
          // que ya sabe procesar rq y devolver #token=...
          const url = new URL(window.location.href);
          url.searchParams.delete("rq");
          const qs = url.searchParams.toString();
          const next = url.pathname + (qs ? `?${qs}` : "");

          const callbackUrl = `${API_BASE}/sso/ezproxy/callback?next=${encodeURIComponent(next)}&rq=${encodeURIComponent(rq)}`;
          window.location.replace(callbackUrl);
        })();

        return;
      }
    }

    /* =========================
       LOGIN NORMAL (SSO)
       ========================= */

    const noEsAnon = location.pathname !== "/anon";

    if (!bypassLogin && sinSesion && noEsAnon && !esInvitado) {
      const next = `${location.pathname}${location.search}`;
      const destino = buildEzproxyLoginUrl(next);
      window.location.replace(destino);
    }
  }, [location.search, location.pathname, location.hash, navigate]);

  if (ezStatus === "loading") {
    return <LoadingIndicator message="Procesando sesion EZproxy..." />;
  }

  if (ezStatus === "error") {
    return <p className="p-8 text-center">{ezMotivo || "No se pudo procesar la sesión EZproxy."}</p>;
  }

  if (inviteStatus === "loading") {
    return <LoadingIndicator message="Validando invitacion..." />;
  }

  if (inviteStatus === "invalido") {
    return <PantallaErrorInvitado motivo={motivo} />;
  }

  return (
    <Routes>
      <Route path="/" element={<InicioNormal />} />
      <Route path="/anon" element={<InicioAnonimo />} />
      <Route path="/categoria/:nombreCategoria" element={<CategoriaPage />} />
      <Route path="/recurso/:id" element={<RecursoPage />} />
      <Route path="*" element={<InicioNormal />} />
    </Routes>
  );
}

/* =========================
   APP ROOT
   ========================= */

export default function App() {
  if (MODO_MANTENIMIENTO) {
    return <MaintenanceScreen />;
  }

  return (
    <BrowserRouter>
      <RoutedApp />
    </BrowserRouter>
  );
}




